/* eslint-disable camelcase */
import Loader from '../../components/Loader';
import {T} from '../../utils/I18n/I18n';
import {useTranslation} from "react-i18next";
import clsx from 'clsx';
import React, { useContext } from 'react';
import { useQuery } from '@apollo/client';
import CheckoutXSvg from '../../assets/icons/checkout-x.svg';
import Pencil from '../../assets/icons/edit-pencil.png';
import { useCartContext } from '../../context/CartContext';
import { STORE_LOCATIONS } from '../../queries/misc';
import formatLocalePrice from '../../utils/formatLocalePrice';
import emmezetaPlaceholder from '../../assets/img/emmezeta-placeholder.png';
import readConfig from '../../utils/readClientConfiguration';
import productionConfiguration from '../../config/default.json';
import calculateNcz30dPercentage from '../../utils/calculateNcz30dPercentage';
import calculateOriginalPrice from '../../utils/calculateSavings';
import Prices from '../../components/BaseCurrencyPrices/Prices';
import './Checkout.scss';
import CartSummary from "../../components/Cart/CartSummary/CartSummary";

const calculateSavings = (cart, discounts, originalPrice, countryCode) => {
  // Non-EU logic
  if(countryCode === "sr-RS") {
    let sumSavings = cart.items.reduce((sum, item) => {
      if (item !== null) {
        return sum - +item.product.prices[0].amount * item.quantity;
      }
      return 0;
    }, originalPrice);

    if (discounts) {
      discounts.forEach(discount => {
        sumSavings += discount.amount.value;
      });
    }

    if (sumSavings < 1e-3) {
      sumSavings = 0;
    }

    return sumSavings;
  }

  // EU logic

  let savings = cart.items.reduce((sum, item) => {
    if (item !== null) {
      return sum - +item.product.prices[0].amount * item.quantity;
    }
    return 0;
  }, originalPrice);

  const discountSum = discounts && discounts.reduce((sum, discount) => sum + discount.amount.value, 0);

  if (discountSum) {
    savings += discountSum;
  }

  if (savings < 1e-3) {
    savings = 0;
  }


  return savings;
};

const MiniCartItem = ({ item }) => {

  const countryCode = readConfig(productionConfiguration, "countryCode");

  const {
    quantity,
    product: {
      sku,
      name,
      weight,
      thumbnail,
      advanced_product_inventory,
      prices: [{ price_type, amount, old_amount, percentage, xumulus_rule_label }],
      price_last_30
    },
  } = item;
  let firstImage = '';
  let enableQtyIncrements = '0';
  let unit = '';
  if (advanced_product_inventory && advanced_product_inventory.enable_qty_increments) {
    enableQtyIncrements = advanced_product_inventory.enable_qty_increments;
    unit = advanced_product_inventory.unit;
  }

  const priceNumberFormater = amoutToFormat => {
    return formatLocalePrice(amoutToFormat);
  };

  const renderPrice = () => {
    // Show RS style when loyalty or RS
    if (price_type === 'simple_loyalty' || countryCode === 'sr-RS') {
      return (
        <div className={clsx('mini-cart-item__info__price')}>
          <T id="cart.price" />: <Prices amount={amount} isMinicart />{' '}
          <span className={old_amount && 'mini-cart-item__info__price discounted-price'}>
            <Prices amount={old_amount} isMinicart />
          </span>
        </div>
      );
    }

    // Show discount if price_last_30 less than amount
    if (price_last_30 > amount) {
      return (
        <div className={clsx('mini-cart-item__info__price')}>
          <p>
            <T id="cart.price" />: <Prices amount={amount} isMinicart isInfoPrice />{' '}
            <p>
              <span>
                <T id="checkout.lowestPrice" />
                <Prices amount={price_last_30} pl30 />
              </span>
            </p>
          </p>
        </div>
      );
    }

    // Show normal price
    return (
      <div className={clsx('mini-cart-item__info__price')}>
        <p>
          <T id="cart.price" />: <Prices amount={amount} isMinicart />
        </p>
      </div>
    );
  };

  const renderPrecentage = () => {
    if((percentage && countryCode === "sr-RS") || (percentage && (price_type === "simple_loyalty"))) {
      return (<div className="mini-cart-item__info__savings-percentage">
        <T id="product.discount" />: {percentage}%
      </div>);
    }

    if(+price_last_30 < +amount) {
      return null;
    }

    const calculatedPercentage = calculateNcz30dPercentage(amount, price_last_30);




    // Don't show percentage if below specified value
    if (calculatedPercentage < 1e-3) {
      return null;
    }

    return (<div className="mini-cart-item__info__savings-percentage">
      <T id="product.discount" />: {calculatedPercentage}%
    </div>);
  }
  function removeCacheSegment(url) {
    return url.replace(/\/cache\/[a-zA-Z0-9]{32}\//, '/');
  }
  return (
    <div className="mini-cart-item">
      <div className="mini-cart-item__image">
        {thumbnail && thumbnail.url && <img src={removeCacheSegment(thumbnail.url)} alt={thumbnail.label} />}
      </div>

      <div className="mini-cart-item__info">
        <p className="fw-400">{name}</p>

        {renderPrice()}

        <p>
          <T id="cart.quantity" />: {quantity} {enableQtyIncrements === '1' ? unit : ''}
        </p>
        <p>
          <T id="product.code" />: {sku}
        </p>
        <p>
          <T id="product.weight" />: {weight} kg
        </p>

        {(countryCode === "sr-RS") && xumulus_rule_label && (
          <div className="mini-cart-item__info__xumulus-label">
            <p>{xumulus_rule_label}</p>
          </div>
        )}

        {renderPrecentage()}
      </div>
    </div>
  );
};

const CheckoutCartOverlay = ({ onXClick, onEditClick, checkoutStep }) => {
  const countryCode = readConfig(productionConfiguration, 'countryCode');

  const { t } = useTranslation();
  const { cart, loading } = useCartContext();
  const { data: locations } = useQuery(STORE_LOCATIONS);
  if (loading) {
    return (
      <div className="checkout-cart-overlay">
        <Loader style={{ height: '100%' }} />
      </div>
    );
  }

  const {
    subtotal_including_tax: subtotal,
    subtotal_original_including_tax: subtotal_original_including_tax,
    applied_taxes: appliedTaxes,
    grand_total: grandTotal,
    discounts,
  } = cart.prices;

  if (cart.items.length === 0) {
    return <></>;
  }

  const originalPrice = calculateOriginalPrice(countryCode === 'sr-RS', cart);
  const sumSavings = calculateSavings(cart, discounts, originalPrice, countryCode);

  const shippingAddress = cart && cart.shipping_addresses && cart.shipping_addresses[0];
  const shippingMethod = shippingAddress && shippingAddress.selected_shipping_method;

  const taxes = appliedTaxes.reduce((sum, appliedTax) => {
    return sum + appliedTax.amount.value;
  }, 0);

  const storeLocations = locations && locations.storeLocations;

  const location =
    shippingMethod &&
    shippingMethod.carrier_code === 'storepickup' &&
    storeLocations &&
    storeLocations.find(store => store.city === shippingAddress.city);

  return (
    <div className="checkout-cart-overlay">
      <div className="checkout-cart-overlay__title">
        <span>
          <T id="orders.checkOrder" />
        </span>
        {onXClick && <img className="pointer" onClick={onXClick} src={CheckoutXSvg} alt="X" />}
      </div>
      <CartSummary/>

      <hr />

      <div className="checkout-cart-overlay__items">
        <div className="mini-cart-title">
          {cart.total_quantity} <T id="headerCart.totalQuantity" />:
        </div>

        {cart.items.map(item => (
          <MiniCartItem key={item.id} item={item} />
        ))}
      </div>

      {shippingAddress && shippingMethod && (
        <>
          <hr className="mt-12" />

          <div className="shipping-address mt-12">
            <div className="shipping-address__title">
              <p>
                {shippingMethod.carrier_code === 'storepickup' && <T id="cartSummary.receiveItemInStore" />}
                {shippingMethod.carrier_code === 'matrixrate' && <T id="cartSummary.deliveryOnAddress" />}
              </p>

              {checkoutStep !== 1 && (
                <img
                  className="pointer"
                  src={Pencil}
                  alt=""
                  onClick={onEditClick}
                  title={t('address.editShippingAddress')}
                />
              )}
            </div>

            <p>
              {shippingAddress.firstname} {shippingAddress.lastname}
            </p>
            <p>{shippingAddress.street[0]}</p>
            <p>
              {shippingAddress.city}, {shippingAddress.region && shippingAddress.region.label}{' '}
              {shippingAddress.postcode}
            </p>
            <p>{shippingAddress.country.code === 'HR' ? 'Hrvatska' : ''}</p>
            {shippingAddress.telephone !== '111111111' && <p>{shippingAddress.telephone}</p>}


            {location && (
              <p>
                <T id="checkout.workingHours" />: {location.workingHours}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CheckoutCartOverlay;
